import { useMemo, useState } from "react";
import { APIError, clearToken, getRole, getToken, login, PLATFORM_ADMIN, setSession } from "./api";
import { locales, localeLabels, makeT, resolveLocale, storeLocale } from "./i18n";
import { Recipients } from "./views/Recipients";
import { TipPages } from "./views/TipPages";
import { Payouts } from "./views/Payouts";
import { Gamification } from "./views/Gamification";
import { Stats } from "./views/Stats";
import { Backoffice } from "./views/backoffice/Backoffice";

type Tab = "recipients" | "tip_pages" | "payouts" | "gamification" | "stats";

export default function App() {
  const [locale, setLocale] = useState(resolveLocale);
  const t = useMemo(() => makeT(locale), [locale]);
  const [authed, setAuthed] = useState(() => getToken() !== null);
  const [role, setRole] = useState(getRole);

  if (!authed) {
    return (
      <Login
        t={t}
        locale={locale}
        setLocale={setLocale}
        onAuthed={(r) => {
          setRole(r);
          setAuthed(true);
        }}
      />
    );
  }
  const logout = () => {
    clearToken();
    setAuthed(false);
  };
  // The platform admin's token opens only the backoffice, so it gets only the
  // backoffice's screens — the venue tabs would all answer 403.
  if (role === PLATFORM_ADMIN) {
    return <Backoffice t={t} locale={locale} localeToggle={<LocaleToggle locale={locale} setLocale={setLocale} />} onLogout={logout} />;
  }
  return <Shell t={t} locale={locale} setLocale={setLocale} onLogout={() => setAuthed(false)} />;
}

// The options come from the catalogs rather than a hand-written list. The
// hand-written one offered English and Russian only, so the Spanish strings
// this dashboard already had could not be chosen at all — a translation is not
// shipped until something in the interface can reach it.
function LocaleToggle({ locale, setLocale }: { locale: string; setLocale: (l: string) => void }) {
  return (
    <select
      className="locale"
      value={locale}
      onChange={(e) => {
        storeLocale(e.target.value);
        setLocale(e.target.value);
      }}
    >
      {locales.map((l) => (
        <option key={l} value={l}>
          {localeLabels[l] ?? l}
        </option>
      ))}
    </select>
  );
}

function Login({
  t,
  locale,
  setLocale,
  onAuthed,
}: {
  t: ReturnType<typeof makeT>;
  locale: string;
  setLocale: (l: string) => void;
  onAuthed: (role: string) => void;
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      const { token, user } = await login(email, password);
      setSession(token, user.role);
      onAuthed(user.role);
    } catch (e) {
      setError(t(e instanceof APIError && e.status === 429 ? "login.too_many" : "login.error"));
    }
  }

  return (
    <div className="login-screen">
      <form className="login-card" onSubmit={submit}>
        <div className="login-head">
          <h1>{t("app.title")}</h1>
          <LocaleToggle locale={locale} setLocale={setLocale} />
        </div>
        <input type="email" placeholder={t("login.email")} value={email} onChange={(e) => setEmail(e.target.value)} />
        <input
          type="password"
          placeholder={t("login.password")}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <button type="submit" disabled={!email || !password}>
          {t("login.submit")}
        </button>
        {error && <p className="error">{error}</p>}
      </form>
    </div>
  );
}

function Shell({
  t,
  locale,
  setLocale,
  onLogout,
}: {
  t: ReturnType<typeof makeT>;
  locale: string;
  setLocale: (l: string) => void;
  onLogout: () => void;
}) {
  const [tab, setTab] = useState<Tab>("recipients");
  const tabs: Tab[] = ["recipients", "tip_pages", "payouts", "gamification", "stats"];

  return (
    <div className="shell">
      <header className="topbar">
        <strong>{t("app.title")}</strong>
        <nav>
          {tabs.map((tb) => (
            <button key={tb} className={tab === tb ? "active" : ""} onClick={() => setTab(tb)}>
              {t(`nav.${tb}`)}
            </button>
          ))}
        </nav>
        <div className="topbar-right">
          <LocaleToggle locale={locale} setLocale={setLocale} />
          <button
            className="logout"
            onClick={() => {
              clearToken();
              onLogout();
            }}
          >
            {t("nav.logout")}
          </button>
        </div>
      </header>
      <main>
        {tab === "recipients" && <Recipients t={t} />}
        {tab === "tip_pages" && <TipPages t={t} />}
        {tab === "payouts" && <Payouts t={t} />}
        {tab === "gamification" && <Gamification t={t} />}
        {tab === "stats" && <Stats t={t} />}
      </main>
    </div>
  );
}
